import { useEffect, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  Tiny hash router — keeps navigation state in the URL so the        */
/*  browser Back button, reload, and deep links all work.              */
/*                                                                     */
/*  Hash routing (not history/path) so Cloudflare Pages never 404s a   */
/*  deep link: the server only ever serves `/`, the app reads the      */
/*  fragment. Examples:                                                */
/*    #/overview                                                       */
/*    #/tenancies                                                      */
/*    #/tenancies/<id>                                                 */
/*    #/tenancies/<id>/documents                                       */
/*    #/ledger  #/documents  #/maintenance  #/reminders  #/settings    */
/*    tenant: #/home  #/statement  #/docs  #/requests                  */
/* ------------------------------------------------------------------ */

export type Route = { section: string; id?: string; tab?: string };

export type NavOptions = { replace?: boolean };

/** Parse `location.hash` into `{ section, id, tab }`. Empty hash → empty section. */
export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const [section = '', id, tab] = raw.split('/').filter(Boolean);
  return { section, id, tab };
}

/** Normalise a caller-supplied path (`#/ledger`, `/ledger`, `ledger`) to `#/…`. */
function toHash(path: string): string {
  let p = path.trim();
  if (p.startsWith('#')) p = p.slice(1);
  if (!p.startsWith('/')) p = `/${p}`;
  return `#${p}`;
}

export function useRoute() {
  const [route, setRoute] = useState<Route>(() =>
    parseHash(typeof window !== 'undefined' ? window.location.hash : ''),
  );

  useEffect(() => {
    const onHash = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  /**
   * Navigate to a hash path. Push (default) adds a history entry so the
   * browser Back button returns here; `{ replace: true }` swaps the current
   * entry (used for the default route on load — no phantom back step).
   */
  const navigate = (path: string, opts: NavOptions = {}) => {
    const hash = toHash(path);
    if (opts.replace) {
      // replaceState does NOT fire `hashchange`, so sync state ourselves.
      const url = window.location.pathname + window.location.search + hash;
      window.history.replaceState(null, '', url);
      setRoute(parseHash(hash));
    } else if (hash === window.location.hash) {
      // Same hash never fires `hashchange`; keep state in sync anyway.
      setRoute(parseHash(hash));
    } else {
      // Assigning the hash pushes a history entry AND fires `hashchange`.
      window.location.hash = hash;
    }
  };

  /** Step back through browser history (keeps the Back button in sync). */
  const back = () => window.history.back();

  return { route, navigate, back };
}
